import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import {
  createTenantSipCredential,
  createTenantSipPagingZoneAdmin,
  updateTenantSipPagingZoneAdmin,
  deleteTenantSipCredential,
  getTenantSipConfig,
  getTenantSipPagingZonesAdmin,
  listTenantSipCredentials,
  resetTenantSipCredentialPassword,
} from '../../../api/backendApi';
import {
  buildSipUri,
  isDuplicateSipUserError,
  parseApiErrorMessage,
} from './sipPagingZoneHelpers';
import { SipZoneDialog } from './SipPagingTab';
import { buildSipUserRows, isValidTwilioSipUsername, suggestTwilioSipUsername } from './sipUserList';

function PasswordDialog({ revealed, onClose }) {
  if (!revealed?.password) return null;
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{revealed.title || 'SIP password'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This password is shown once. Copy it now for the device that will register as this SIP user.
        </Alert>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Username: <strong>{revealed.username || '—'}</strong>
        </Typography>
        {revealed.sipUri ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            SIP URI: <strong>{revealed.sipUri}</strong>
          </Typography>
        ) : null}
        <TextField
          label="Password"
          fullWidth
          value={revealed.password}
          InputProps={{ readOnly: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(revealed.password || '');
            } catch {
              // Password stays visible if clipboard is blocked.
            }
          }}
        >
          Copy password
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddSipUserDialog({ open, onClose, tenantId, domain, canCreate, existingUsernames = [], onCreated }) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setUsername('');
    setUsernameTouched(false);
    setError('');
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || usernameTouched) return;
    setUsername(suggestTwilioSipUsername(name));
  }, [open, name, usernameTouched]);

  const sipUri = buildSipUri(suggestTwilioSipUsername(username), domain);

  const handleCreate = async () => {
    const nextName = name.trim();
    const nextUser = suggestTwilioSipUsername(username);
    if (!nextName) {
      setError('Name is required');
      return;
    }
    if (!isValidTwilioSipUsername(nextUser)) {
      setError('Username must be 1–32 characters: start with a letter or number, then letters, numbers, hyphens, or underscores. Paste sip:8057@domain if needed — only 8057 is used.');
      return;
    }
    if (existingUsernames.includes(nextUser)) {
      setError(`SIP user “${nextUser}” already exists on this tenant. Use a different username.`);
      return;
    }
    if (!canCreate) {
      setError('Set the SIP domain and credential list on the SIP tab before creating users.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await createTenantSipCredential(tenantId, {
        username: nextUser,
        label: nextName,
      });
      onCreated({
        title: 'Twilio SIP user created',
        username: created.username || nextUser,
        password: created.password,
        sipUri: buildSipUri(created.username || nextUser, domain),
      });
      onClose();
    } catch (e) {
      setError(
        isDuplicateSipUserError(e)
          ? `SIP user “${nextUser}” already exists.`
          : parseApiErrorMessage(e) || 'Failed to create the SIP user in Twilio.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add SIP User</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Creates a SIP user in Twilio on this tenant’s credential list, the same way a SIP paging zone does.
          </Typography>
          {!canCreate && (
            <Alert severity="warning">
              Tenant SIP Interworking needs a domain and credential list before Twilio users can be created.
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Username"
            fullWidth
            value={username}
            onChange={(e) => {
              setUsernameTouched(true);
              setUsername(e.target.value);
            }}
            onBlur={() => setUsername(suggestTwilioSipUsername(username))}
            helperText={sipUri ? `Registers as ${sipUri}` : 'Username only (e.g. 8057), not the full SIP URI'}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving || !canCreate}>
          {saving ? 'Creating Twilio user…' : 'Create SIP User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SipUsersTab({ tenantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domain, setDomain] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [zoneDialogInitial, setZoneDialogInitial] = useState(null);
  const [zoneDialogSaving, setZoneDialogSaving] = useState(false);
  const [zones, setZones] = useState([]);
  const [revealed, setRevealed] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const [config, credentials, zones] = await Promise.all([
        getTenantSipConfig(tenantId),
        listTenantSipCredentials(tenantId),
        getTenantSipPagingZonesAdmin(tenantId).catch(() => []),
      ]);
      const nextDomain = String(config?.domainFqdn || config?.domain || '').trim();
      setDomain(nextDomain);
      setCanCreate(Boolean(nextDomain && config?.credentialListSid));
      setZones(Array.isArray(zones) ? zones : []);
      setRows(buildSipUserRows({
        credentials,
        zones,
        domain: nextDomain,
      }));
    } catch (e) {
      setError(parseApiErrorMessage(e) || 'Failed to load SIP users');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tenantId]);

  const handleResetPassword = async (row) => {
    const ok = window.confirm(
      `Generate a new password for ${row.username || row.label}? The current password will stop working.`
    );
    if (!ok) return;
    setBusyKey(row.key);
    setError('');
    try {
      const result = await resetTenantSipCredentialPassword(tenantId, {
        username: row.username,
        credentialSid: row.credentialSid || undefined,
      });
      setRevealed({
        title: 'New SIP password',
        username: result.username || row.username,
        password: result.password,
        sipUri: row.sipUri,
      });
      await load();
    } catch (e) {
      setError(parseApiErrorMessage(e) || 'Failed to generate a new SIP password');
    } finally {
      setBusyKey('');
    }
  };

  const handleDelete = async (row) => {
    if (!row.credentialSid && !row.username) return;
    const ok = window.confirm(
      `Delete SIP user ${row.username || row.label}? This removes the Twilio credential.`
    );
    if (!ok) return;
    setBusyKey(row.key);
    setError('');
    try {
      await deleteTenantSipCredential(tenantId, row.credentialSid, { username: row.username });
      await load();
    } catch (e) {
      setError(parseApiErrorMessage(e) || 'Failed to delete SIP user');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <Box sx={{ mt: 1 }} data-testid="sip-users-tab">
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" component="h2">
              SIP Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Twilio SIP users for this tenant. Add or edit paging zones on the SIP Page Zone tab.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setZoneDialogInitial(null);
                setZoneDialogOpen(true);
              }}
              disabled={!tenantId}
            >
              Add SIP Page Zone
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddOpen(true)}
              disabled={!tenantId}
            >
              Add SIP User
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No SIP users yet.
          </Typography>
        ) : (
          <Table size="small" data-testid="sip-users-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>SIP URI</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.username || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.kind}
                      color={row.kind === 'SIP Page Zone' ? 'primary' : 'default'}
                      variant={row.kind === 'SIP User' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.sipUri || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      {row.kind === 'SIP Page Zone' && row.zoneId ? (
                        <Button
                          size="small"
                          onClick={() => {
                            const zone = zones.find((z) => (z.id || z.zoneId) === row.zoneId);
                            setZoneDialogInitial(zone || {
                              id: row.zoneId,
                              name: row.label,
                              sipUri: row.sipUri,
                            });
                            setZoneDialogOpen(true);
                          }}
                        >
                          Edit zone
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        onClick={() => handleResetPassword(row)}
                        disabled={!row.username || busyKey === row.key}
                      >
                        New password
                      </Button>
                      {row.credentialSid || row.username ? (
                        <Button
                          size="small"
                          color="error"
                          aria-label={`Delete ${row.username || row.label}`}
                          onClick={() => handleDelete(row)}
                          disabled={busyKey === row.key}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <AddSipUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        tenantId={tenantId}
        domain={domain}
        canCreate={canCreate}
        existingUsernames={rows.map((row) => String(row.username || '').trim().toLowerCase()).filter(Boolean)}
        onCreated={(next) => {
          setRevealed(next);
          load();
        }}
      />
      <SipZoneDialog
        open={zoneDialogOpen}
        onClose={() => {
          setZoneDialogOpen(false);
          setZoneDialogInitial(null);
        }}
        onSubmit={async (payload) => {
          if (!tenantId) return;
          setZoneDialogSaving(true);
          try {
            if (zoneDialogInitial?.id) {
              await updateTenantSipPagingZoneAdmin(tenantId, zoneDialogInitial.id, payload);
            } else {
              await createTenantSipPagingZoneAdmin(tenantId, payload);
            }
            await load();
            setZoneDialogOpen(false);
            setZoneDialogInitial(null);
          } finally {
            setZoneDialogSaving(false);
          }
        }}
        initial={zoneDialogInitial}
        tenantId={tenantId}
        saving={zoneDialogSaving}
      />
      <PasswordDialog revealed={revealed} onClose={() => setRevealed(null)} />
    </Box>
  );
}
